import React, { useState, useEffect } from 'react';
import { Badge, Dropdown, List, Space, Button, Tag, Typography, Empty } from 'antd';
import {
  BellOutlined,
  ExclamationCircleOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchUserNotifications,
  markNotificationAsRead,
  SystemNotification
} from '../../store/slices/notificationSlice';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text } = Typography;

const NotificationBell = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { notifications, unreadCount } = useAppSelector(
    state => state.notifications
  );

  useEffect(() => {
    dispatch(fetchUserNotifications({ unread: true }));
  }, [dispatch]);

  const handleNotificationClick = (notification: SystemNotification) => {
    dispatch(markNotificationAsRead(notification.id));
    setOpen(false);
    navigate('/notifications');
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'red';
      case 'high':
        return 'orange';
      case 'normal':
        return 'blue';
      default:
        return 'green';
    }
  };

  const notificationMenu = (
    <div className="bg-white rounded-lg shadow-lg w-96 max-h-[70vh] flex flex-col">
      <div className="p-4 border-b">
        <Text strong>Notifications</Text>
      </div>
      
      <div className="overflow-y-auto flex-grow">
        <List
          dataSource={notifications.slice(0, 5)}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No new notifications"
              />
            )
          }}
          renderItem={(item) => (
            <List.Item
              className="cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => handleNotificationClick(item)}
            >
              <List.Item.Meta
                avatar={
                  item.type === 'emergency' ? (
                    <ExclamationCircleOutlined className="text-red-500" />
                  ) : (
                    <BellOutlined />
                  )
                }
                title={
                  <Space>
                    <Text strong>{item.title}</Text>
                    <Tag color={getPriorityColor(item.priority)}>
                      {item.priority.toUpperCase()}
                    </Tag>
                  </Space>
                }
                description={
                  <Text type="secondary" className="text-sm">
                    {dayjs(item.start_date).fromNow()}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      </div>

      <div className="p-4 border-t">
        <Button
          type="link"
          block
          onClick={() => {
            setOpen(false);
            navigate('/notifications');
          }}
        >
          <Space>
            <span>View All Notifications</span>
            <ArrowRightOutlined />
          </Space>
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown
      overlay={notificationMenu}
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <Badge count={unreadCount}>
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: '20px' }} />}
          className="flex items-center justify-center w-10 h-10"
        />
      </Badge>
    </Dropdown>
  );
};

export default NotificationBell;